import type { ComponentReview } from '../../audio/personalMessagesApi';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  CaptureClient,
  CaptureSessionConfig,
  CaptureTarget,
  captureTargetsEqual,
} from '../../audio/captureClient';
import {
  PcmRecorder,
  RecorderInitError,
  RecorderInitFailureKind,
  TakeStats,
  recorderInitFailureKind,
} from '../../audio/recorder';
import { MIC_CHECK, TAKE_PROMPTS, VERBATIM_PROMPTS } from '../../audio/prompts';
import { FormField, PillButton, RecordingControl, Sheet, StateMessage } from '../ui';
import { captureReducer, initialCaptureState, CaptureMode } from './captureMachines';
import { clearCaptureDraft, readCaptureDraft, writeCaptureDraft } from './captureDraft';
import { createCaptureDiagnostics } from '../../audio/captureDiagnostics';
import './GuidedCapture.css';

interface GuidedCaptureProps {
  client: CaptureClient;
  target: CaptureTarget;
  voiceLabel: string;
  speakerDisplayLabel: string;
  listenerDisplayLabel?: string;
  onComplete: () => void;
  onCancel: () => void;
  completionKind?: 'build' | 'capture';
  collectVoiceLabel?: boolean;
}

const safeFailure = 'That recording could not be saved. Your progress is still here. Try the same take again.';
type SoundCheckResult = 'pass' | 'warn' | 'block' | 'clipping';
type CaptureStartFailureKind = RecorderInitFailureKind | 'session';
const microphoneFailureCopy: Record<CaptureStartFailureKind, { title: string; message: string }> = {
  permission: {
    title: 'Luma can’t access your microphone',
    message: 'Allow microphone access for readluma.com in Safari, then try again.',
  },
  device: {
    title: 'Your microphone is unavailable',
    message: 'Close any other app using the microphone, then try again. Your saved progress is safe.',
  },
  engine: {
    title: 'Safari didn’t start recording',
    message: 'Try the microphone again. If it still does not start, reload this page once. Your saved progress is safe.',
  },
  session: {
    title: 'Luma could not confirm this capture',
    message: 'Your saved progress is safe. Keep this page open and try again in a moment.',
  },
};
type CaptureReview =
  | { scope: 'take'; itemIndex: number; status: 'accepted' | 'rejected' | 'save_error'; attempts: number; reason?: string }
  | { scope: 'clip'; clipKey: 'intro' | 'name' | 'goodnight'; status: 'accepted' | 'rejected' | 'save_error'; attempts: number; reason?: string };

function safeQaReason(reasons?: string[] | null, personalMessage = false): string {
  const reason = (reasons || []).join(' ').toLowerCase();
  if (personalMessage && /too long/.test(reason)) return 'Keep your message under 20 seconds, then try recording again.';
  if (personalMessage && /silence/.test(reason)) return 'Read the whole message with fewer long pauses, then try again.';
  if (/clip|too loud|loud/.test(reason)) return 'The microphone was too close.';
  if (/noise|snr|background/.test(reason)) return 'The room was too noisy.';
  if (/quiet|hear|rms/.test(reason)) return 'We couldn’t hear enough speech.';
  if (/short|beginning/.test(reason)) return 'The beginning was cut off.';
  return 'Let’s record that passage once more.';
}

const GuidedCapture: React.FC<GuidedCaptureProps> = ({
  client,
  target,
  voiceLabel,
  speakerDisplayLabel,
  listenerDisplayLabel,
  onComplete,
  onCancel,
  completionKind = 'build',
  collectVoiceLabel = true,
}) => {
  const diagnosticClientRef = useRef(client);
  diagnosticClientRef.current = client;
  const [diagnostics] = useState(() => createCaptureDiagnostics(
    (event) => diagnosticClientRef.current.emitDiagnostic?.(event), target.kind,
  ));
  useEffect(() => { diagnostics.opened(); }, [diagnostics]);
  const mode: CaptureMode = target.kind === 'voice'
    ? 'voice_creation'
    : 'listener_personalization';
  const [capture, send] = useReducer(captureReducer, mode, initialCaptureState);
  const [voiceName, setVoiceName] = useState(voiceLabel);
  const [activeTarget, setActiveTarget] = useState<CaptureTarget>(target);
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const [soundCheckRunning, setSoundCheckRunning] = useState(false);
  const [soundCheckPhase, setSoundCheckPhase] = useState<'room' | 'speech'>('room');
  const [soundCheckResult, setSoundCheckResult] = useState<SoundCheckResult | null>(null);
  const [sessionConfig, setSessionConfig] = useState<CaptureSessionConfig | null>(null);
  const [lastBlob, setLastBlob] = useState<Blob | null>(null);
  const [lastStats, setLastStats] = useState<TakeStats | null>(null);
  const [failedTakeIndex, setFailedTakeIndex] = useState<number | null>(null);
  const [failedClipKey, setFailedClipKey] = useState<'intro' | 'name' | 'goodnight' | null>(null);
  const [review, setReview] = useState<CaptureReview | null>(null);
  const [takeAttempts, setTakeAttempts] = useState<Record<number, number>>({});
  const [clipAttempts, setClipAttempts] = useState<Record<string, number>>({});
  const [progressRetry, setProgressRetry] = useState(0);
  const [progressFailed, setProgressFailed] = useState(false);
  const [progressLoading, setProgressLoading] = useState(false);
  const [pendingReviews, setPendingReviews] = useState<ComponentReview[]>([]);
  const [componentReview, setComponentReview] = useState<ComponentReview | null>(null);
  const [reviewListened, setReviewListened] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const approvalRequestRef = useRef<string | null>(null);
  const [reviewAudioUrl, setReviewAudioUrl] = useState<string | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [microphoneFailure, setMicrophoneFailure] = useState<CaptureStartFailureKind>('permission');
  // Presentation-only clock for the record control; the recorder owns audio.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousPromptRef = useRef('intro');
  const recorderRef = useRef<PcmRecorder | null>(null);
  const isVoiceCreation = mode === 'voice_creation';
  const asksForVoiceLabel = isVoiceCreation && collectVoiceLabel;
  const voiceId = activeTarget.voiceId || '';
  const listenerId = activeTarget.kind === 'listener'
    ? activeTarget.listenerId
    : undefined;
  const exactListenerLabel = listenerDisplayLabel;
  const takePrompts = useMemo(() => {
    const configured = sessionConfig?.takePrompts;
    if (!configured?.length) return TAKE_PROMPTS;
    return configured.map((prompt) => ({
      style: prompt.style,
      title: prompt.title,
      coaching: prompt.coaching,
      text: prompt.text,
      minS: prompt.minS,
      maxS: prompt.maxS,
    }));
  }, [sessionConfig]);
  const verbatimPrompts = useMemo(() => {
    const configured = sessionConfig?.verbatimPrompts;
    const prompts = !configured?.length ? VERBATIM_PROMPTS : configured.map((prompt) => ({
      key: prompt.key,
      title: prompt.title,
      coaching: prompt.coaching,
      template: (childName: string, parentName: string) => prompt.template
        .replace(/\{child_name\}/g, childName)
        .replace(/\{parent_name\}/g, parentName),
    }));
    return client.requiresComponentReview ? prompts.filter(prompt => prompt.key !== 'name') : prompts;
  }, [sessionConfig, client.requiresComponentReview]);
  const take = takePrompts[capture.takeIndex];
  const clipIndex = verbatimPrompts.findIndex((prompt) => !capture.completedClipKeys.includes(prompt.key));
  const clip = clipIndex >= 0 ? verbatimPrompts[clipIndex] : undefined;
  const reviewClip = review?.scope === 'clip'
    ? verbatimPrompts.find((prompt) => prompt.key === review.clipKey)
    : undefined;
  const activeClip = review?.scope === 'clip' ? reviewClip : clip;
  const hasConfirmedProgress = capture.completedTakeIndexes.length > 0 || capture.completedClipKeys.length > 0;
  const isRecordingStep = capture.step === 'takes' || capture.step === 'bookends';
  const savedCount = isVoiceCreation ? capture.completedTakeIndexes.length : capture.completedClipKeys.length;
  const recordingCount = isVoiceCreation ? takePrompts.length : verbatimPrompts.length;
  const promptFocusKey = `${capture.step}:${capture.takeIndex}:${activeClip?.key || ''}`;

  useEffect(() => {
    if (!lastBlob) {
      setReviewAudioUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(lastBlob);
    setReviewAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [lastBlob]);

  useEffect(() => {
    let mounted = true;
    void client.fetchSessionConfig().then((config) => {
      if (mounted) setSessionConfig(config);
    }).catch(() => {
      if (mounted) setSessionConfig(null);
    });
    return () => { mounted = false; };
  }, [client]);

  useEffect(() => {
    if (!voiceId && client.voiceTargetRequiresId) return undefined;
    const requestedTarget = activeTarget;
    const draft = readCaptureDraft(
      client.draftScopeKey,
      voiceId,
      listenerId,
    );
    if (draft && !client.requiresComponentReview) {
      setResumed(draft.persistedSampleIndexes.length > 0 || draft.persistedVerbatimKeys.length > 0);
      send({
        type: 'RESUME_PROGRESS',
        takeIndexes: draft.persistedSampleIndexes.map((index) => index - 1),
        clipKeys: draft.persistedVerbatimKeys,
      });
    }
    let mounted = true;
    setProgressLoading(true);
    void client.fetchProgress(requestedTarget).then((status) => {
      if (!mounted || !captureTargetsEqual(status.target, requestedTarget)) return;
      setProgressFailed(false);
      if (progressRetry) send({type: 'RETRY'});
      send({
        type: 'RESUME_PROGRESS',
        takeIndexes: status.completedTakeIndexes,
        clipKeys: status.completedClipKeys,
      });
      setResumed(status.completedTakeIndexes.length > 0 || status.completedClipKeys.length > 0 || !!status.pendingReviews?.length);
      setPendingReviews(status.pendingReviews || []);
      const pending = status.pendingReviews?.[0];
      if (pending && client.requiresComponentReview) {
        setComponentReview(pending); setReviewListened(false); setLastBlob(null);
        setReview({scope: 'clip', clipKey: pending.kind, status: 'accepted', attempts: 0});
        send({type: 'RETRY'});
        void client.loadReviewAudio?.(requestedTarget, pending).then(blob => {
          if (mounted) setLastBlob(blob);
        }).catch(() => { if (mounted) setReviewError('We could not load your saved message. Try again.'); });
      }
    }).catch(() => {
      if (mounted && client.requiresComponentReview) {
        setProgressFailed(true);
        send({type: 'FAIL', message: 'We could not check your saved messages. Please try again.'});
      }
    }).finally(() => { if (mounted) setProgressLoading(false); });
    return () => { mounted = false; };
  }, [activeTarget, client, listenerId, voiceId, progressRetry]);

  useEffect(() => () => { recorderRef.current?.dispose(); recorderRef.current = null; }, []);

  useEffect(() => {
    if (previousPromptRef.current === promptFocusKey) return undefined;
    previousPromptRef.current = promptFocusKey;
    const timer = window.setTimeout(() => stepHeadingRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [promptFocusKey]);

  useEffect(() => {
    if (!review || saving) return undefined;
    const timer = window.setTimeout(() => reviewHeadingRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [review, saving]);

  useEffect(() => {
    if (!recording) return undefined;
    setElapsedSeconds(0);
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 500);
    return () => window.clearInterval(timer);
  }, [recording]);

  useEffect(() => {
    if (!recording && !soundCheckRunning) return undefined;
    let interrupted = false;
    const interrupt = () => {
      if (interrupted) return;
      interrupted = true;
      diagnostics.emit('recording_attempt_failed', { failure_code: 'recording_failed' });
      recorderRef.current?.dispose();
      recorderRef.current = null;
      if (soundCheckRunning) {
        setSoundCheckRunning(false);
        setSoundCheckResult('block');
        return;
      }
      setRecording(false);
      setSaving(false);
      send({ type: 'FAIL', message: 'Recording was interrupted. Nothing was saved. Please record this step again.' });
    };
    const onVisibilityChange = () => { if (document.visibilityState === 'hidden') interrupt(); };
    const onDeviceChange = () => interrupt();
    document.addEventListener('visibilitychange', onVisibilityChange);
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
    };
  }, [recording, soundCheckRunning, diagnostics]);

  useEffect(() => {
    if (client.voiceTargetRequiresId && !voiceId) return;
    writeCaptureDraft(client.draftScopeKey, {
      version: 1,
      kind: isVoiceCreation ? 'voice' : 'listener_personalization',
      voiceId: voiceId || null,
      listenerId,
      persistedSampleIndexes: capture.completedTakeIndexes.map((index) => index + 1),
      persistedVerbatimKeys: capture.completedClipKeys,
      updatedAt: new Date().toISOString(),
    });
  }, [capture.completedClipKeys, capture.completedTakeIndexes, client.draftScopeKey, client.voiceTargetRequiresId, isVoiceCreation, listenerId, voiceId]);

  const getRecorder = async (): Promise<PcmRecorder> => {
    if (recorderRef.current) return recorderRef.current;
    const recorder = new PcmRecorder();
    recorder.onLevel(() => undefined);
    try {
      await recorder.init();
      recorderRef.current = recorder;
      return recorder;
    } catch (error) {
      recorder.dispose();
      throw error;
    }
  };

  const begin = async () => {
    if (asksForVoiceLabel && !voiceName.trim()) return;
    if (capture.step === 'permission' && capture.error) diagnostics.emit('recording_retry_requested', { retry_kind: 'record' });
    diagnostics.start('microphone');
    send({ type: 'BEGIN_PERMISSION' });
    setMicrophoneFailure('permission');
    setSoundCheckPhase('room');
    setSoundCheckResult(null);
    try {
      // Keep recorder initialization directly attached to the user's tap.
      // In particular, the guest progress preflight must not consume Safari's
      // transient audio activation before AudioContext is created.
      await getRecorder();
    } catch (error) {
      const kind = recorderInitFailureKind(error);
      diagnostics.emit(kind === 'permission' ? 'microphone_permission_denied' : 'recording_attempt_failed', { failure_code: kind === 'permission' ? 'permission_denied' : kind === 'device' ? 'device_unavailable' : 'recording_failed' });
      setMicrophoneFailure(kind);
      send({ type: 'PERMISSION_DENIED', message: microphoneFailureCopy[kind].message });
      return;
    }
    try {
      if (isVoiceCreation && !voiceId && client.voiceTargetRequiresId) {
        const created = await client.ensureVoice(voiceName.trim());
        if (
          created.kind !== 'voice'
          || (client.voiceTargetRequiresId && !created.voiceId)
        ) throw new Error('voice target');
        setActiveTarget(created);
      }
      send({ type: 'PERMISSION_GRANTED' });
    } catch {
      recorderRef.current?.dispose();
      recorderRef.current = null;
      diagnostics.emit('recording_attempt_failed', { failure_code: 'unknown' });
      setMicrophoneFailure('session');
      send({ type: 'PERMISSION_DENIED', message: microphoneFailureCopy.session.message });
    }
  };

  const runSoundCheck = async () => {
    const recorder = recorderRef.current;
    if (!recorder || soundCheckRunning) return;
    diagnostics.start(soundCheckPhase === 'room' ? 'room_check' : 'speech_check');
    setSoundCheckRunning(true);
    try {
      await recorder.start();
      const seconds = soundCheckPhase === 'room'
        ? sessionConfig?.micCheck.roomToneS ?? MIC_CHECK.roomToneS
        : sessionConfig?.micCheck.speechS ?? MIC_CHECK.speechS;
      await new Promise((resolve) => window.setTimeout(resolve, seconds * 1000));
      const output = await recorder.stop();
      const maxPeak = sessionConfig?.micCheck.peakMaxDb ?? -1;
      const clipped = output.stats.clippedFrames > 0 || output.stats.peakDb > maxPeak + 0.5;
      let result: SoundCheckResult = 'pass';
      if (clipped) result = 'clipping';
      else if (soundCheckPhase === 'room') {
        if (output.stats.rawRmsDb > -35) result = 'block';
        else if (output.stats.rawRmsDb > -45) result = 'warn';
      } else if (output.stats.rawRmsDb < -50) result = 'block';
      else if (output.stats.rawRmsDb < -42) result = 'warn';
      setSoundCheckResult(result);
    } catch (error) {
      diagnostics.emit('recording_attempt_failed', { failure_code: 'recording_failed' });
      if (error instanceof RecorderInitError) {
        recorder.dispose();
        recorderRef.current = null;
        setMicrophoneFailure('engine');
        send({ type: 'PERMISSION_DENIED', message: microphoneFailureCopy.engine.message });
      } else {
        setSoundCheckResult('block');
      }
    } finally { setSoundCheckRunning(false); }
  };

  const continueSoundCheck = (allowWarning = false) => {
    if (!soundCheckResult || (soundCheckResult === 'warn' && !allowWarning)) return;
    if (soundCheckResult === 'block' || soundCheckResult === 'clipping') {
      diagnostics.emit('recording_retry_requested', { retry_kind: 'record' });
      setSoundCheckResult(null);
      return;
    }
    if (soundCheckPhase === 'room') {
      setSoundCheckPhase('speech');
      setSoundCheckResult(null);
    } else {
      setSoundCheckResult(null);
      send({ type: 'SOUND_CHECK_PASSED', requiredClipKeys: verbatimPrompts.map(prompt => prompt.key) });
    }
  };

  const saveTake = async (blob: Blob, stats: TakeStats, index: number, forced = false) => {
    const prompt = takePrompts[index];
    if (activeTarget.kind !== 'voice' || !prompt) return;
    setSaving(true); send({ type: 'TAKE_UPLOAD_STARTED' });
    try {
      const result = await client.uploadTake(activeTarget, {
        sampleIndex: index + 1,
        style: prompt.style,
        stats,
        voiceLabel: voiceName.trim() || undefined,
        forced,
      }, blob);
      if (!result.storageConfirmed || (!result.accepted && !forced)) {
        diagnostics.emit('recording_save_failed', { failure_code: 'save_failed' });
        const attempts = (takeAttempts[index] || 0) + 1;
        setTakeAttempts((current) => ({ ...current, [index]: attempts }));
        send({ type: 'TAKE_UPLOAD_FAILED' });
        setFailedTakeIndex(index);
        setReview({ scope: 'take', itemIndex: index, status: 'rejected', attempts, reason: safeQaReason(result.rejectionReasons) });
        setLastBlob(blob); setLastStats(stats);
        return;
      }
      send({ type: 'TAKE_UPLOAD_ACCEPTED', index });
      setFailedTakeIndex(null); setLastBlob(blob); setLastStats(stats);
      setReview({ scope: 'take', itemIndex: index, status: 'accepted', attempts: takeAttempts[index] || 0 });
    } catch {
      diagnostics.emit('recording_save_failed', { failure_code: 'save_failed' });
      send({ type: 'TAKE_UPLOAD_FAILED' });
      setFailedTakeIndex(index); setLastBlob(blob); setLastStats(stats);
      setReview({ scope: 'take', itemIndex: index, status: 'save_error', attempts: takeAttempts[index] || 0 });
    }
    finally { setSaving(false); }
  };

  const startTake = async () => {
    if (recording || !take) return;
    diagnostics.start('passage');
    try { const recorder = await getRecorder(); setRecording(true); await recorder.start(); } catch (error) { diagnostics.emit(recorderInitFailureKind(error) === 'permission' ? 'microphone_permission_denied' : 'recording_attempt_failed', { failure_code: recorderInitFailureKind(error) === 'permission' ? 'permission_denied' : 'recording_failed' }); setRecording(false); send({ type: 'FAIL', message: 'The microphone paused. Tap record to try again.' }); }
  };

  const finishTake = async () => {
    const recorder = recorderRef.current;
    if (!recorder || !recording || !take) return;
    setRecording(false);
    try { const output = await recorder.stop(); setLastBlob(output.wavBlob); setLastStats(output.stats); await saveTake(output.wavBlob, output.stats, capture.takeIndex); }
    catch { diagnostics.emit('recording_attempt_failed', { failure_code: 'recording_failed' }); send({ type: 'FAIL', message: safeFailure }); }
  };

  const retryTake = async () => {
    if (lastBlob && lastStats && failedTakeIndex != null) { diagnostics.emit('recording_retry_requested', { retry_kind: 'save' }); setReview(null); send({ type: 'RETRY' }); await saveTake(lastBlob, lastStats, failedTakeIndex); }
  };

  const saveClip = async (blob: Blob, key: 'intro' | 'name' | 'goodnight', forced = false) => {
    if (activeTarget.kind !== 'listener') return;
    setSaving(true); send({ type: 'TAKE_UPLOAD_STARTED' });
    try {
      const result = await client.uploadListenerClip(activeTarget, {
        clipKey: key,
        forced,
      }, blob);
      if (!result.storageConfirmed || (!result.accepted && !forced)) {
        diagnostics.emit('recording_save_failed', { failure_code: 'save_failed' });
        const attempts = (clipAttempts[key] || 0) + 1;
        setClipAttempts((current) => ({ ...current, [key]: attempts }));
        send({ type: 'BOOKEND_UPLOAD_FAILED' });
        setFailedClipKey(key);
        setReview({ scope: 'clip', clipKey: key, status: 'rejected', attempts, reason: safeQaReason(result.rejectionReasons, client.requiresComponentReview === true) });
        setLastBlob(blob);
        return;
      }
      if (client.requiresComponentReview && key !== 'name') {
        if (!result.componentReview || !client.loadReviewAudio) throw new Error('Saved review receipt missing');
        setComponentReview(result.componentReview); setReviewListened(false); setReviewError(null);
        approvalRequestRef.current = null;
        setLastBlob(null);
        send({type: 'BOOKEND_UPLOAD_FAILED'});
        try { setLastBlob(await client.loadReviewAudio(activeTarget, result.componentReview)); }
        catch { setReviewError('We could not load your saved message. Try again.'); }
      } else {
        send({ type: 'BOOKEND_UPLOAD_ACCEPTED', key }); setLastBlob(blob);
      }
      setFailedClipKey(null);
      setReview({ scope: 'clip', clipKey: key, status: 'accepted', attempts: clipAttempts[key] || 0 });
    } catch {
      diagnostics.emit('recording_save_failed', { failure_code: 'save_failed' });
      send({ type: 'BOOKEND_UPLOAD_FAILED' });
      setFailedClipKey(key); setLastBlob(blob);
      setReview({ scope: 'clip', clipKey: key, status: 'save_error', attempts: clipAttempts[key] || 0 });
    }
    finally { setSaving(false); }
  };

  const startClip = async () => {
    if (recording || !clip) return;
    diagnostics.start('listener_clip');
    try { const recorder = await getRecorder(); setRecording(true); await recorder.start(); } catch (error) { diagnostics.emit(recorderInitFailureKind(error) === 'permission' ? 'microphone_permission_denied' : 'recording_attempt_failed', { failure_code: recorderInitFailureKind(error) === 'permission' ? 'permission_denied' : 'recording_failed' }); setRecording(false); send({ type: 'FAIL', message: 'The microphone paused. Tap record to try again.' }); }
  };

  const finishClip = async () => {
    const recorder = recorderRef.current;
    if (!recorder || !recording || !clip) return;
    setRecording(false);
    try { const output = await recorder.stop(); setLastBlob(output.wavBlob); setLastStats(output.stats); await saveClip(output.wavBlob, clip.key); }
    catch { diagnostics.emit('recording_attempt_failed', { failure_code: 'recording_failed' }); send({ type: 'FAIL', message: safeFailure }); }
  };

  const retryClip = async () => {
    if (lastBlob && failedClipKey) { diagnostics.emit('recording_retry_requested', { retry_kind: 'save' }); setReview(null); send({ type: 'RETRY' }); await saveClip(lastBlob, failedClipKey); }
  };

  const reloadSavedReview = async () => {
    if (!componentReview || !client.loadReviewAudio) return;
    setSaving(true); setReviewError(null);
    try { setLastBlob(await client.loadReviewAudio(activeTarget, componentReview)); }
    catch { setReviewError('We could not load your saved message. Try again.'); }
    finally { setSaving(false); }
  };

  const advanceReview = async () => {
    if (!review || review.status !== 'accepted') return;
    const completedReview = review;
    if (client.requiresComponentReview && review.scope === 'clip' && review.clipKey !== 'name') {
      if (!componentReview || !reviewListened || !client.approveRecording) return;
      setSaving(true); setReviewError(null);
      try {
        if (!approvalRequestRef.current) approvalRequestRef.current = crypto.randomUUID();
        await client.approveRecording(activeTarget, componentReview, approvalRequestRef.current);
        send({type: 'BOOKEND_UPLOAD_ACCEPTED', key: review.clipKey});
        const remaining = pendingReviews.filter(item => item.recording_version_id !== componentReview.recording_version_id);
        setPendingReviews(remaining);
        setComponentReview(null); setReviewListened(false); approvalRequestRef.current = null;
        if (remaining.length && client.loadReviewAudio) {
          const next = remaining[0];
          setComponentReview(next); setLastBlob(null);
          setReview({scope: 'clip', clipKey: next.kind, status: 'accepted', attempts: 0});
          try { setLastBlob(await client.loadReviewAudio(activeTarget, next)); }
          catch { setReviewError('We could not load your saved message. Try again.'); }
          return;
        }
      } catch { setReviewError('We could not confirm your review. Your message is saved. Try again.'); return; }
      finally { setSaving(false); }
    }
    setReview(null);
    setLastBlob(null);
    setLastStats(null);
    setFailedTakeIndex(null);
    setFailedClipKey(null);
    if (completedReview.scope === 'take') {
      send({ type: 'NEXT_TAKE' });
    } else if (completedReview.clipKey === 'goodnight') {
      send({ type: 'OPEN_REVIEW' });
    }
  };

  const recordReviewAgain = () => {
    if (!review || (review.status === 'accepted' && !componentReview)) return;
    if (componentReview) {
      setPendingReviews(items => items.filter(item => item.recording_version_id !== componentReview.recording_version_id));
      setComponentReview(null); setReviewListened(false); setReviewError(null); approvalRequestRef.current = null;
    }
    diagnostics.emit('recording_retry_requested', { retry_kind: 'record' });
    const retryReview = review;
    setReview(null);
    setLastBlob(null);
    setLastStats(null);
    if (retryReview.scope === 'take') {
      setFailedTakeIndex(retryReview.itemIndex);
      send({ type: 'RETRY' });
    } else {
      setFailedClipKey(retryReview.clipKey);
      send({ type: 'RETRY' });
    }
  };

  const saveReviewAgain = () => {
    if (!review || review.status !== 'save_error' || !lastBlob) return;
    diagnostics.emit('recording_retry_requested', { retry_kind: 'save' });
    if (review.scope === 'take' && lastStats) void saveTake(lastBlob, lastStats, review.itemIndex);
    if (review.scope === 'clip') void saveClip(lastBlob, review.clipKey);
  };

  const keepReview = () => {
    if (!review || review.status !== 'rejected' || review.attempts < 3 || !lastBlob) return;
    diagnostics.emit('recording_retry_requested', { retry_kind: 'save' });
    if (review.scope === 'take' && lastStats) void saveTake(lastBlob, lastStats, review.itemIndex, true);
    if (review.scope === 'clip') void saveClip(lastBlob, review.clipKey, true);
  };

  const train = async () => {
    if (activeTarget.kind !== 'voice') return;
    send({ type: 'START_TRAINING' });
    try {
      const status = await client.fetchProgress(activeTarget);
      const complete = captureTargetsEqual(status.target, activeTarget)
        && [0, 1, 2, 3, 4, 5].every((index) => status.completedTakeIndexes.includes(index));
      if (!complete) {
        send({ type: 'FAIL', message: 'We’re missing one recording. Your saved recordings are here. Continue with the missing passage.' });
        return;
      }
    } catch {
      send({ type: 'FAIL', message: 'We could not confirm your saved recordings. Try again in a moment.' });
      return;
    }
    try {
      await client.complete(activeTarget);
      clearCaptureDraft(client.draftScopeKey, voiceId);
      send({ type: 'COMPLETE' });
    } catch {
      send({
        type: 'FAIL',
        message: completionKind === 'capture'
          ? 'We could not confirm every saved recording. Your saved takes are safe. Try again.'
          : 'The voice build could not start. Your saved takes are safe. Try again.',
      });
    }
  };

  const saveListener = async () => {
    if (activeTarget.kind !== 'listener') return;
    setSaving(true);
    try {
      const status = await client.fetchProgress(activeTarget);
      const requiredKeys = verbatimPrompts.map(prompt => prompt.key);
      if (!captureTargetsEqual(status.target, activeTarget) || requiredKeys.some((key) => !status.completedClipKeys.includes(key))) throw new Error('bookends');
      await client.complete(activeTarget);
      clearCaptureDraft(client.draftScopeKey, voiceId, activeTarget.listenerId);
      send({ type: 'COMPLETE' });
      window.setTimeout(onComplete, 400);
    } catch { send({ type: 'FAIL', message: 'Luma could not confirm the hello and goodnight. Nothing was changed; try again.' }); }
    finally { setSaving(false); }
  };

  const requestCancel = () => {
    if (hasConfirmedProgress) setConfirmingCancel(true);
    else { diagnostics.emit('recording_cancelled', { cancel_kind: 'exit' }); onCancel(); }
  };
  const discardSession = () => {
    diagnostics.emit('recording_cancelled', { cancel_kind: 'discard' });
    clearCaptureDraft(client.draftScopeKey, voiceId, listenerId);
    setConfirmingCancel(false);
    onCancel();
  };

  const header = (withFocusTarget: boolean) => (
    <header className="guided-capture__header">
      <h1 ref={withFocusTarget && !isRecordingStep ? stepHeadingRef : undefined} tabIndex={-1}>
        {isVoiceCreation ? 'A voice for bedtime' : `A personal hello for ${exactListenerLabel || 'your listener'}`}
      </h1>
      {!isRecordingStep && (
        <p>
          {isVoiceCreation
            ? 'Record six short passages, then add personal messages.'
            : client.requiresComponentReview ? 'Record a hello and goodnight for this listener.' : 'Record a hello, name and goodnight for this listener.'}
        </p>
      )}
    </header>
  );

  const cancelSheet = (
    <Sheet open={confirmingCancel} title="Save your progress?" onClose={() => setConfirmingCancel(false)}>
      <p>{completionKind === 'capture'
        ? 'Your saved recordings stay available. Clearing this browser reminder does not delete them.'
        : 'Your confirmed recordings will stay available for this session. You can come back and continue later.'}</p>
      <div className="guided-capture__actions">
        <PillButton
          variant="primary"
          onClick={() => { diagnostics.emit('recording_cancelled', { cancel_kind: 'keep_later' }); setConfirmingCancel(false); onCancel(); }}
        >
          Keep for later
        </PillButton>
        <PillButton variant="destructive" onClick={discardSession}>
          {completionKind === 'capture' ? 'Clear browser reminder' : 'Discard session'}
        </PillButton>
      </div>
    </Sheet>
  );

  if (capture.step === 'done') {
    return (
      <div className="guided-capture">
        <div className="guided-capture__container">
          <section className="guided-capture__done" role="status">
            <h1>{completionKind === 'capture'
              ? isVoiceCreation ? 'Your voice recordings are saved' : `${exactListenerLabel || 'Your listener'}’s messages are saved`
              : isVoiceCreation ? 'Your family voice is being made' : `${exactListenerLabel || 'Your listener'} is ready`}</h1>
            <p>{completionKind === 'capture'
              ? isVoiceCreation ? 'Continue when you’re ready to record personal messages. Voice building has not started yet.' : 'Luma confirmed all three personal moments without rebuilding the voice.'
              : isVoiceCreation ? 'You can leave this page while Luma makes your voice.' : 'The personal messages are ready for bedtime.'}</p>
            <div className="guided-capture__actions">
              <PillButton variant="primary" onClick={onComplete}>
                {completionKind === 'capture' ? 'Continue' : 'Return to Family voices'}
              </PillButton>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (capture.step === 'error') {
    return (
      <div className="guided-capture">
        <div className="guided-capture__container">
          {header(false)}
          <StateMessage kind="error" title="Let’s try that again" message={capture.error || safeFailure} />
          <div className="guided-capture__actions">
            <PillButton
              variant="primary"
              disabled={saving || progressLoading}
              onClick={() => { if (progressFailed) setProgressRetry(value => value + 1); else if (failedTakeIndex != null) void retryTake(); else if (failedClipKey) void retryClip(); else { diagnostics.emit('recording_retry_requested', { retry_kind: 'record' }); send({ type: 'RETRY' }); } }}
            >
              {failedTakeIndex != null ? 'Retry this take' : failedClipKey ? 'Retry this clip' : 'Try again'}
            </PillButton>
            <PillButton variant="secondary" onClick={requestCancel}>{hasConfirmedProgress ? 'Save for later' : 'Exit'}</PillButton>
          </div>
          {cancelSheet}
        </div>
      </div>
    );
  }

  const scriptText = isVoiceCreation
    ? take?.text
    : activeClip?.template(exactListenerLabel || 'your listener', speakerDisplayLabel || 'your family');

  return (
    <div className="guided-capture">
      <div className="guided-capture__container">
        {header(true)}

        {capture.step === 'intro' && (
          <section className="guided-capture__step">
            <h2>{asksForVoiceLabel ? 'Name this voice' : 'Find a quiet spot'}</h2>
            {resumed && <p className="guided-capture__resume" role="status">Your saved recordings are here. {savedCount} of {recordingCount} saved. We’ll check your microphone, then continue where you left off.</p>}
            {asksForVoiceLabel ? (
              <>
                <FormField id="voice-name" label="Voice name">
                  <input value={voiceName} onChange={(event) => setVoiceName(event.target.value)} placeholder="e.g., Mom’s voice" autoComplete="off" />
                </FormField>
                <p className="guided-capture__trust">Only an adult can start this recording. Your name is a label, not permission.</p>
              </>
            ) : isVoiceCreation ? (
              <p className="guided-capture__trust">Your voice label is already saved. This step records only the six approved voice passages.</p>
            ) : (
              <p className="guided-capture__trust">You’re recording only for {exactListenerLabel || 'this listener'} using the existing Family voice. No child can start or edit this step.</p>
            )}
            <div className="guided-capture__actions">
              <PillButton variant="primary" disabled={asksForVoiceLabel && !voiceName.trim()} onClick={() => void begin()}>Check my microphone</PillButton>
              <PillButton variant="secondary" onClick={requestCancel}>Cancel</PillButton>
            </div>
          </section>
        )}

        {capture.step === 'permission' && (capture.error ? (
          <section className="guided-capture__step">
            <StateMessage kind="error" title={microphoneFailureCopy[microphoneFailure].title} message={capture.error} />
            <div className="guided-capture__actions">
              <PillButton variant="primary" onClick={() => void begin()}>Try microphone again</PillButton>
              <PillButton variant="secondary" onClick={onCancel}>Not now</PillButton>
            </div>
          </section>
        ) : (
          <section className="guided-capture__status-block" role="status" aria-live="polite" aria-busy="true">
            <h2>Asking for microphone access…</h2>
            <p>Your microphone is still off until you choose Allow.</p>
          </section>
        ))}

        {capture.step === 'sound_check' && (
          <section className="guided-capture__step">
            <h2>{soundCheckResult
              ? soundCheckResult === 'pass' ? 'This room sounds good' : soundCheckResult === 'warn' ? 'This can work' : soundCheckResult === 'clipping' ? 'The microphone is too close' : 'There’s too much background sound'
              : soundCheckPhase === 'room' ? 'Find a quiet spot' : 'Read this short line'}</h2>
            {soundCheckResult ? (
              <>
                <p>{soundCheckResult === 'pass'
                  ? soundCheckPhase === 'room' ? 'The room is quiet enough. Now we’ll check your bedtime voice.' : 'Your microphone is ready for the first passage.'
                  : soundCheckResult === 'warn' ? 'Moving away from a fan or softening the room may sound better.'
                  : soundCheckResult === 'clipping' ? 'Move the phone a little farther away, then check again.'
                  : 'Try a quieter spot and check the microphone again.'}</p>
                <div className="guided-capture__actions">
                  <PillButton variant="primary" onClick={() => continueSoundCheck(soundCheckResult === 'warn')}>
                    {soundCheckResult === 'pass'
                      ? soundCheckPhase === 'room' ? 'Continue to voice check' : 'Start recording'
                      : soundCheckResult === 'warn' ? soundCheckPhase === 'room' ? 'Continue to voice check' : 'Continue anyway'
                      : soundCheckResult === 'clipping' ? 'Move it farther away' : 'Try a quieter spot'}
                  </PillButton>
                  {soundCheckResult === 'warn' && (
                    <PillButton variant="secondary" onClick={() => { diagnostics.emit('recording_retry_requested', { retry_kind: 'record' }); setSoundCheckResult(null); }}>Check again</PillButton>
                  )}
                </div>
              </>
            ) : (
              <>
                <p>{soundCheckPhase === 'room' ? 'Stay quiet for a few seconds while Luma listens to the room.' : 'Read this line in your bedtime voice.'}</p>
                {soundCheckPhase === 'speech' && <blockquote className="guided-capture__script">{sessionConfig?.micCheck.sentence || MIC_CHECK.sentence}</blockquote>}
                <p className="guided-capture__trust">Luma records only after you tap the button.</p>
                <div className="guided-capture__actions">
                  <PillButton variant="primary" busy={soundCheckRunning} busyLabel="Listening…" onClick={() => void runSoundCheck()}>
                    {soundCheckPhase === 'room' ? 'Start quiet-room check' : 'Check my voice'}
                  </PillButton>
                </div>
              </>
            )}
          </section>
        )}

        {isRecordingStep && (isVoiceCreation ? take : activeClip) && (
          <section className="guided-capture__step guided-capture__step--recording">
            <div className="guided-capture__progress">
              <span>{isVoiceCreation
                ? `Passage ${capture.takeIndex + 1} of ${takePrompts.length}`
                : `Personal message ${review?.scope === 'clip' ? verbatimPrompts.findIndex((prompt) => prompt.key === review.clipKey) + 1 : clipIndex + 1} of ${verbatimPrompts.length}`}</span>
              <span className="guided-capture__saved-count">{savedCount} saved</span>
            </div>
            <progress className="guided-capture__progress-bar" value={savedCount} max={recordingCount} aria-label="Recordings saved" />
            <h2 ref={stepHeadingRef} tabIndex={-1}>{isVoiceCreation ? take.title : activeClip?.title}</h2>
            <p className="guided-capture__coaching">{isVoiceCreation ? take.coaching : activeClip?.coaching}</p>
            {review ? (
              <details className="guided-capture__read-again">
                <summary>Read the words again</summary>
                <blockquote className="guided-capture__script" tabIndex={0} aria-label="Recording passage">{scriptText}</blockquote>
              </details>
            ) : <blockquote className="guided-capture__script" tabIndex={0} aria-label="Recording passage">{scriptText}</blockquote>}
            <div className="guided-capture__controls">
              {saving ? (
                <>
                  <p className="guided-capture__status" role="status">Saving your recording… Keep this page open.</p>
                  <PillButton variant="primary" disabled>Saving recording…</PillButton>
                </>
              ) : review ? (
                <div className="guided-capture__review">
                  <div role="status" aria-live="polite">
                    <h3 ref={reviewHeadingRef} tabIndex={-1}>{review.status === 'accepted' ? 'Recording saved' : review.status === 'save_error' ? 'This recording isn’t saved yet.' : 'Let’s record that once more.'}</h3>
                    <p>{review.status === 'accepted' ? componentReview ? 'Listen to your saved message, then approve it for storytime.' : 'Listen back if you’d like, then continue.' : review.status === 'save_error' ? 'Keep this page open. You can try saving again without re-recording.' : review.reason}</p>
                  </div>
                  {reviewAudioUrl && <audio className="guided-capture__review-audio" controls preload="metadata" src={reviewAudioUrl} onEnded={() => setReviewListened(true)} onError={() => { if (componentReview) { setLastBlob(null); setReviewListened(false); setReviewError('We could not play this message. Try loading it again.'); } }} aria-label={review.scope === 'take' ? `Listen to passage ${review.itemIndex + 1}` : `Listen to ${activeClip?.title}`} />}
                  {reviewError && <p role="alert">{reviewError}</p>}
                  <div className="guided-capture__actions">
                    {review.status === 'accepted' ? (
                      <PillButton variant="primary" disabled={!!componentReview && !reviewListened && !!lastBlob} onClick={componentReview && !lastBlob ? reloadSavedReview : advanceReview}>
                        {componentReview ? !lastBlob ? 'Load saved message' : 'Approve this message' : review.scope === 'take'
                          ? savedCount === recordingCount ? 'Review saved passages' : 'Next passage'
                          : review.clipKey === 'goodnight' ? 'Review saved messages' : 'Next message'}
                      </PillButton>
                    ) : (
                      <>
                        <PillButton variant="primary" onClick={review.status === 'save_error' ? saveReviewAgain : recordReviewAgain}>{review.status === 'save_error' ? 'Try saving again' : 'Record again'}</PillButton>
                        {review.status === 'rejected' && review.attempts >= 3 && !(client.requiresComponentReview && review.scope === 'clip') && <PillButton variant="secondary" className="guided-capture__keep" onClick={keepReview}>Keep this recording</PillButton>}
                        {review.status === 'save_error' && <PillButton variant="secondary" onClick={recordReviewAgain}>Record again</PillButton>}
                      </>
                    )}
                    {review.status === 'accepted' && componentReview && <PillButton variant="secondary" onClick={recordReviewAgain}>Record again</PillButton>}
                  </div>
                </div>
              ) : (
                <>
                  <p className={`guided-capture__status${recording ? ' guided-capture__status--recording' : ''}`} role="status" aria-live="polite">
                    {recording ? 'Recording. Read at your own pace, then tap Stop.' : 'Read at your own pace. Scroll the passage as needed.'}
                  </p>
                  <RecordingControl
                    state={recording ? 'recording' : 'idle'}
                    elapsedSeconds={elapsedSeconds}
                    minSeconds={isVoiceCreation && take && take.minS >= 1 ? take.minS : undefined}
                    maxSeconds={isVoiceCreation && take && take.maxS >= 1 ? take.maxS : undefined}
                    label={isVoiceCreation ? 'Record passage' : `Record ${activeClip?.key}`}
                    onStart={() => void (isVoiceCreation ? startTake() : startClip())}
                    onStop={() => void (isVoiceCreation ? finishTake() : finishClip())}
                  />
                </>
              )}
            </div>
            {hasConfirmedProgress && !recording && !saving && review?.status !== 'save_error' && (
              <PillButton variant="secondary" className="guided-capture__pause" onClick={requestCancel}>Save for later</PillButton>
            )}
          </section>
        )}

        {capture.step === 'review' && (
          <section className="guided-capture__step">
            <h2>{isVoiceCreation ? completionKind === 'capture' ? 'Ready to save these voice recordings?' : 'Ready to build the voice?' : `Review ${exactListenerLabel || 'your listener'}’s personal moments`}</h2>
            <p>{isVoiceCreation
              ? completionKind === 'capture' ? 'Six accepted passages are ready. This step confirms capture only; it does not start a voice build.' : 'Six accepted passages are ready. Building the voice may take a few minutes.'
              : client.requiresComponentReview ? 'Your hello and goodnight are reviewed and ready for storytime.' : 'The existing Family voice stays unchanged. Luma will confirm all three personal moments before finishing.'}</p>
            <ul className="guided-capture__checklist">
              {isVoiceCreation && <li>{capture.completedTakeIndexes.length} of {takePrompts.length} story passages saved</li>}
              {!isVoiceCreation && <li>{capture.completedClipKeys.length} of {verbatimPrompts.length} personal moments saved</li>}
            </ul>
            <div className="guided-capture__actions">
              <PillButton variant="primary" busy={saving} busyLabel="Confirming…" onClick={() => void (isVoiceCreation ? train() : saveListener())}>
                {isVoiceCreation ? completionKind === 'capture' ? 'Finish voice recordings' : 'Make family voice' : 'Save listener'}
              </PillButton>
              <PillButton variant="secondary" onClick={requestCancel}>Save for later</PillButton>
            </div>
          </section>
        )}

        {capture.step === 'training' && (
          <section className="guided-capture__status-block" role="status" aria-live="polite" aria-busy="true">
            <h2>{completionKind === 'capture' ? 'Confirming saved recordings…' : 'Making your family voice…'}</h2>
            <p>{completionKind === 'capture' ? 'Checking that all your recordings are saved.' : 'You can safely leave this page. The voice will appear in Family voices when it is ready.'}</p>
          </section>
        )}

        {cancelSheet}
      </div>
    </div>
  );
};

export default GuidedCapture;
